<script lang="ts">
  import Card, { Content } from '@smui/card';
  import type { Options } from '@splidejs/splide';
  import { Splide, SplideSlide } from '@splidejs/svelte-splide';
  import { afterUpdate } from 'svelte';
  import { APP_CONSTANTS } from '../../../common/config/app-constants';
  import { isDirectionRTL, isMobileWidth } from '../../../common/util';
  import TagContainer from '../../common/components/tag/TagContainer.svelte';
  import Text from '../../common/components/Text.svelte';
  import { filterImagesPreferArabic, getProductImages, onClickWishlist } from '../services/pdp-data-services';
  import { currentProductImage, productDetailMainData, productDetails, productImages } from '../store/pdp_store';
  import PdpGalleryModal from './PdpGalleryModal.svelte';
  import PdpMobileMainTile from './PdpMobileMainTile.svelte';
  import PdpWishlist from './PdpWishlist.svelte';
  import PlyrWrapper from './PlyrWrapper.svelte';
  import ProductReviewHead from './productReviewHead.svelte';

  export let data: any;
  export let loaded: boolean = false;
  export let ProductRatingData;
  export let preorder: boolean = false;
  let filteredProductImages = [];

  let mainImageSlider: Splide;
  let thumbnailSlider: SplideSlide;
  let mainImagePaginationSlider: SplideSlide;

  let showFeatureTileWrapper = true;
  let moreImageCount = 0;
  let productItemImages = [];
  var data360View = [];
  let is360Available = false;

  let videoPlayer;
  let showGalleryModal = false;
  let galleryModalInitialIndex = 0;
  let selectedThumbnailId: string | null = null;
  let currentSku = null;

  let isWhishList = $productDetails.inWhishList === true ? true : false;
  const wishlisted = isWhishList;
  $: wishlistedItemId = $productDetails?.wishlistEntryPk;
  $: imgData = data;

  $: if (imgData?.code && currentSku != imgData?.code) {
    currentSku = imgData?.code;
    fetchProductImages(imgData?.code);
    syncSliders();
    videoPlayer = document.getElementsByTagName('video');
  }

  function syncSliders() {
    if (mainImageSlider?.splide && thumbnailSlider?.splide) {
      mainImageSlider.sync(thumbnailSlider.splide);
    }

    if (mainImageSlider?.splide && mainImagePaginationSlider?.splide) {
      mainImageSlider.sync(mainImagePaginationSlider.splide);
    }
  }

  function handleWishlistChange(data) {
    onClickWishlist(data, isWhishList, wishlistedItemId);
  }
  const isRTL = isDirectionRTL();
  const lang = window['ACC'].config.lang;

  const mainImageSliderOption: Options = {
    type: 'loop',
    arrows: false,
    direction: (isRTL && 'rtl') || 'ltr',
    perPage: 1,
    updateOnMove: true,
    pagination: false,
    wheel: true,
    releaseWheel: true,
    waitForTransition: true,
    mediaQuery: 'min',
    classes: {
      page: 'splide__pagination__page pd-img-splide__pagination__page',
    },
    breakpoints: {
      900: {
        type: 'slide',
        perPage: 1,
        perMove: 1,
        gap: '0rem',
        pagination: false,
        padding: { left: '0rem', right: '0rem' },
        wheel: false,
      },
    },
  };

  const thumbnailSliderOptions: Options = {
    type: 'slide',
    direction: 'ttb',
    height: '20rem',
    perPage: 8,
    arrows: false,
    gap: '8px',
    pagination: false,
    fixedWidth: 56,
    fixedHeight: 56,
    cover: true,
    isNavigation: true,
    wheel: true,
    mediaQuery: 'min',
    releaseWheel: true,
    waitForTransition: true,
    keyboard: false,
    breakpoints: {
      760: {
        perPage: 4,
      },
    },
  };

  if (preorder) {
    thumbnailSliderOptions.direction = (isRTL && 'rtl') || 'ltr';
    thumbnailSliderOptions.fixedWidth = 72;
    thumbnailSliderOptions.fixedHeight = 72;
    thumbnailSliderOptions.gap = '12px';
    thumbnailSliderOptions.perPage = 7;
    thumbnailSliderOptions.focus = 'center';
    thumbnailSliderOptions.drag = true;
    thumbnailSliderOptions.rewind = true;
    thumbnailSliderOptions.updateOnMove = true;
  }

  const mainSliderPaginationOptions: Options = {
    type: 'slide',
    arrows: false,
    direction: (isRTL && 'rtl') || 'ltr',
    pagination: false,
    fixedWidth: 4,
    fixedHeight: 4,
    waitForTransition: true,
  };

  const fetchProductImages = async productCode => {
    try {
      await getProductImages(productCode);
      // Filter images to prefer Arabic versions when available
      filteredProductImages = filterImagesPreferArabic($productImages, lang);

      data360View = data?.extraProductGalleryTiles.filter(val => val.code === '360_VIEW');
      const image360View = filteredProductImages.filter(val => val.type === 'set');
      is360Available = image360View.length && data360View.length ? true : false;
      if (isMobileWidth()) {
        productItemImages = filteredProductImages.slice(0, is360Available ? 5 : 6);
      } else {
        productItemImages = filteredProductImages.slice(0, is360Available ? 7 : 8);
      }
      if (preorder) {
        productItemImages = filteredProductImages;
      }
      selectedThumbnailId = is360Available ? '360' : (filteredProductImages[0]?.src ?? null);
      loaded = true;
    } catch {
      console.log('Error while Ampliance call to get Product images');
    }
  };

  /*
      Method to handle hide and show of side bar(feature tile wraper on side of images) on moving to next
      image
    */
  const shouldShoFeatureTiles = data => {
    if (data.detail.index == 0) {
      showFeatureTileWrapper = true;
    } else {
      showFeatureTileWrapper = false;
    }
  };

  function getCurrentThumbnailKeyFromSlideIndex(slideIndex: number) {
    if (is360Available) {
      return slideIndex === 0 ? '360' : (filteredProductImages[slideIndex - 1]?.src ?? null);
    }
    return filteredProductImages[slideIndex]?.src ?? null;
  }

  function handleMainSliderMove(event) {
    shouldShoFeatureTiles(event);
    const slideIndex = event?.detail?.index;
    if (slideIndex != null) {
      selectedThumbnailId = getCurrentThumbnailKeyFromSlideIndex(slideIndex);
    }
  }

  const getMoreThumbnailImage = () => {
    productItemImages = [...filteredProductImages];
    moreImageCount = 0;
  };

  const updateMoreBtnLabel = () => {
    if (preorder) {
      return false;
    }
    let shownSlides = 0;
    if (isMobileWidth()) {
      shownSlides = is360Available ? 5 : 6;
    } else {
      shownSlides = is360Available ? 7 : 8;
    }
    if (filteredProductImages.length > shownSlides) moreImageCount = filteredProductImages.length - shownSlides;
  };

  afterUpdate(() => {
    data360View = data?.extraProductGalleryTiles.filter(val => val.code === '360_VIEW');
    filteredProductImages = filterImagesPreferArabic($productImages, lang);
    const image360View = filteredProductImages.filter(val => val.type === 'set');
    is360Available = image360View.length && data360View.length ? true : false;
    if (isMobileWidth()) {
      productItemImages = filteredProductImages.slice(0, is360Available ? 5 : 6);
    }
  });

  function openGalleryModal(index = 0) {
    galleryModalInitialIndex = index;
    showGalleryModal = true;
  }

  function selectThumbnail(thumbKey: string | null, index = 0, is360 = false) {
    selectedThumbnailId = thumbKey;
    currentProductImage.set(thumbKey ?? '');

    const imageIndex = thumbKey ? filteredProductImages.findIndex(image => image.src === thumbKey) : -1;
    const targetIndex = is360
      ? 0
      : is360Available
        ? imageIndex >= 0
          ? imageIndex + 1
          : index + 1
        : imageIndex >= 0
          ? imageIndex
          : index;

    if (mainImageSlider?.splide) {
      mainImageSlider.splide.go(targetIndex);
    }

    if (videoPlayer) {
      for (let i = 0; i < videoPlayer.length; i++) {
        videoPlayer[i].pause();
      }
    }
  }

  function handleThumbMoved(event: CustomEvent) {
    if (!preorder) {
      return false;
    }
    const thumbKey = event?.detail?.index;
    const currentImg = filteredProductImages?.[thumbKey]?.src;
    selectThumbnail(currentImg, thumbKey);
  }
</script>

{#if !loaded}
  <div class="pd-image-container skeleton-pdp-image">
    <div class="splide-wrapper">
      <div class="hide-on-mobile skeleton-item skeleton-thumbnail-image"></div>
      <div class="side-wrapper {showFeatureTileWrapper ? 'hide-on-desktop' : 'd-none'} h-100 position-absolute"></div>
      <div class=" skeleton-item skeleton-image-wrapper images-wrapper flex-grow-1 h-100"></div>
    </div>
  </div>
{:else}
  <section class="pd-image-container">
    {#if preorder}
      <TagContainer productTags={data?.productTags} />
    {/if}
    <div class="tags-container wishlist_icon" class:rtl={isRTL} class:hide-on-mobile={!preorder}>
      <PdpWishlist
        {wishlisted}
        on:changeWishList={data => {
          handleWishlistChange(data);
        }}
      />
    </div>

    {#if productItemImages.length}
      <div class="splide-wrapper" class:c_preorderthumb={preorder}>
        <div class="hide-on-mobile thumbimgs">
          <div class="thumbnail-wrapper">
            <Splide
              options={{ ...thumbnailSliderOptions }}
              bind:this={thumbnailSlider}
              on:mounted={() => updateMoreBtnLabel()}
              on:moved={handleThumbMoved}
            >
              {#if is360Available}
                <SplideSlide>
                  <div
                    class="view-360-thumb"
                    class:is-active={selectedThumbnailId === '360'}
                    role="button"
                    tabindex="0"
                    on:mouseover={() => {
                      if (!preorder) {
                        selectThumbnail('360', 0, true);
                      }
                    }}
                    on:focus={() => {
                      if (!preorder) {
                        selectThumbnail('360', 0, true);
                      }
                    }}
                    on:keydown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        selectThumbnail('360', 0, true);
                      }
                    }}
                    on:click={() => selectThumbnail('360', 0, true)}
                  >
                    {#if data360View?.length && data360View[0]}
                      <Card class="view-360">
                        {#if data360View[0].imageUrl !== null}
                          <img class="card-image" src={data360View[0].imageUrl + '?fmt=auto&sm=c'} alt={data?.name} />
                        {/if}
                        <Content>{data360View[0].label}</Content>
                      </Card>
                    {/if}
                  </div>
                </SplideSlide>
              {/if}
              {#each productItemImages as image, index}
                <SplideSlide>
                  <!-- svelte-ignore a11y-click-events-have-key-events -->
                  <div
                    class="thumbnail-item"
                    class:is-active={selectedThumbnailId === image.src}
                    role="button"
                    tabindex="0"
                    on:mouseover={() => {
                      if (!preorder) {
                        selectThumbnail(image.src, index);
                      }
                    }}
                    on:focus={() => {
                      if (!preorder) {
                        selectThumbnail(image.src, index);
                      }
                    }}
                    on:click={() => selectThumbnail(image.src, index)}
                  >
                    {#if image.type == 'video'}
                      <span class="video_play_btn"></span>
                    {/if}
                    <picture>
                      <img src={image.src + '?fmt=auto&w=109&sm=c'} alt={data?.name} />
                    </picture>
                  </div>
                </SplideSlide>
              {/each}
            </Splide>
            {#if moreImageCount && !preorder}
              <div class="more-item-btn__wrapper">
                <button on:click={() => getMoreThumbnailImage()}>
                  +{moreImageCount}
                  <Text key="product.view.thumb.more" />
                </button>
              </div>
            {/if}
          </div>
        </div>

        <!-- Mobile-view: Main tile components -->
        {#if !$productDetailMainData.isMarketPlaceProduct}
          <div class="side-wrapper {showFeatureTileWrapper ? 'hide-on-desktop' : 'd-none'} h-100 position-absolute">
            <PdpMobileMainTile keyHighlights={data?.keyHighlights} />
          </div>
        {/if}
        <div class="product_review_rating_section hide-on-desktop">
          {#if !preorder}
            <ProductReviewHead {ProductRatingData} />
          {/if}
          {#if !preorder}
            <PdpWishlist
              {wishlisted}
              on:changeWishList={data => {
                handleWishlistChange(data);
              }}
            />
          {/if}
        </div>

        {#key !preorder || (imgData?.code && crypto.randomUUID())}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div class="images-wrapper flex-grow-1 h-100">
            <div class="position-relative">
              <div class="main-slider-wrapper justify-content-center align-items-center" id="prodImage-container">
                <Splide
                  options={mainImageSliderOption}
                  bind:this={mainImageSlider}
                  on:move={handleMainSliderMove}
                  on:mounted={syncSliders}
                >
                  <!-- {#if is360Available && !isMobileWidth()} -->
                  {#if is360Available}
                    <!-- enable 360 view for mobile similar to web view -->
                    <SplideSlide>
                      <div class="splide__slide__container">
                        <!-- svelte-ignore a11y-missing-attribute -->
                        <object
                          class="product-image-360"
                          type="text/html"
                          data={APP_CONSTANTS.IMAGE_360_VIEW_BASE_URL.replace('productSku', data?.code)}
                        ></object>
                      </div>
                    </SplideSlide>
                  {/if}
                  {#each filteredProductImages as image, index}
                    <SplideSlide>
                      <div class="splide__slide__container">
                        {#if image.type == 'video'}
                          {@const source = [
                            image.src + '/mp4_480p',
                            image.src + '/mp4_1080p',
                            image.src + '/webm_480p',
                            image.src + '/webm_1080p',
                          ]}

                          <PlyrWrapper poster={image.src} {source} muted={false} controls={true} />
                        {:else}
                          <div class="product-image-container" on:click={() => openGalleryModal(index)}>
                            <picture>
                              <img
                                id="prodImage"
                                class="product-image {!data?.keyHighlights?.length ? 'no-keyHighlight' : ''}"
                                src={image.src + '?fmt=auto&w=721&sm=c'}
                                alt={data?.name}
                              />
                            </picture>
                          </div>
                        {/if}
                      </div>
                    </SplideSlide>
                  {/each}
                </Splide>
              </div>
            </div>
          </div>

          <!--  -->
          <div class="hide-on-desktop">
            <div class="slide-pagination-wrapper">
              <Splide
                options={{ ...mainSliderPaginationOptions }}
                bind:this={mainImagePaginationSlider}
                on:mounted={syncSliders}
              >
                {#each filteredProductImages as _item}
                  <SplideSlide>
                    <div class="pagination-lable"></div>
                  </SplideSlide>
                {/each}
              </Splide>
            </div>
          </div>
        {/key}
        <!--  -->
      </div>
    {/if}
  </section>
{/if}
<PdpGalleryModal bind:open={showGalleryModal} initialIndex={galleryModalInitialIndex} />

<style lang="scss">
  @import '../../../common/styles/vars';

  @import '../../common/styles/colors.new.scss';

  :global {
    .view-360 {
      &.mdc-card {
        width: 56px;
        height: 56px;
        justify-content: center;
        align-items: center;
        text-align: center;
        box-shadow: none;
        border-radius: 12px;
        background-color: transparent;
      }
      .smui-card__content {
        padding: 0px;
        text-align: center;
        font-size: 10px;
        font-weight: 400;
        line-height: 11px;
        width: 50px;
        height: 11px;
      }
    }

    .pd-image-container {
      position: relative;

      @include desktop-min-width {
        padding-right: 48px;
        padding-left: 48px;
        padding-top: 56px;
        margin-top: 0;
      }

      .tags-container {
        position: absolute;
        right: 64px;
        top: 72px;
        z-index: 1;
        display: flex;
        gap: 8px;
      }
      .splide-wrapper {
        position: relative;
        .splide__slide {
          @include desktop-max-width {
            &.is-next .splide__slide__container {
              display: flex;
              align-items: center;
              justify-content: flex-start;
              transform: scale(0.5) translatex(-40%);
              opacity: 0.6;
              transition:
                opacity 1200ms ease-out,
                transform 600ms ease-out;
              will-change: opacity, transform;
              &.rtl {
                transform: scale(0.5) translatex(40%);
              }
            }
            &.is-prev .splide__slide__container {
              display: flex;
              justify-content: flex-end;
              align-items: center;
              transform: scale(0.5) translatex(40%);
              opacity: 0.6;
              transition:
                opacity 1200ms ease-out,
                transform 600ms ease-out;
              will-change: opacity, transform;
              &.rtl {
                transform: scale(0.5) translatex(-40%);
              }
            }
            &.is-active .splide__slide__container {
              display: flex;
              justify-content: center;
              align-items: center;
              transform: scale(1);
              opacity: 1;
              transition:
                opacity 1200ms ease-out,
                transform 600ms ease-out;
              will-change: opacity, transform;
            }
            &:nth-child(1) {
              &.is-active {
                .product-image {
                  margin-left: 56px;
                  // margin-left: 13%;
                  &.rtl {
                    margin-left: unset;
                    margin-right: 56px;
                    // margin-right: 13%;
                  }
                }
              }
            }
          }
        }

        /* css to add loop effect of slide and handliing side view of images on mobile screen */
        .main-slider-wrapper {
          .splide.splide--draggable {
            &.splide--rtl {
              direction: unset;
            }
          }
          .splide.splide--loop {
            &.splide--rtl {
              direction: unset;
            }
            .splide__track {
              @include desktop-max-width {
                width: 100vw;
                .splide__list {
                  .splide__slide {
                    &[id$='-slide01'] {
                      &.is-active {
                        .product-image {
                          margin-left: 56px;
                          // margin-left: 13%;
                          &.no-keyHighlight {
                            margin-left: 0px;
                            // margin-left: 0%;
                            &.rtl {
                              margin-left: unset;
                              margin-right: 0px;
                              // margin-right: 0%;
                            }
                          }
                          &.rtl {
                            margin-left: unset;
                            margin-right: 56px;
                            // margin-right: 13%;
                          }
                        }
                      }
                    }
                    &.splide__slide--clone {
                      visibility: hidden;
                    }
                  }
                }
              }
            }
          }
        }
        .thumbnail-wrapper {
          .splide {
            &.splide--ttb {
              .splide__track {
                height: 570px !important;

                .splide__list .splide__slide {
                  border-radius: 8px;
                  background: $primary-white;
                  border: none;
                }
              }
            }
          }
          .card-image {
            padding: 0;
            width: 40px;
            height: 40px;
          }
          .view-360-thumb {
            &.is-active {
              border-radius: 8px;
              border: 1px solid $primary-blue;
            }
          }
          .thumbnail-item {
            height: 100%;
            justify-content: center;
            display: flex;
            overflow: hidden;
            border-radius: 8px;
            &.is-active {
              border: 1px solid $primary-blue;
            }
            img {
              height: 100%;
              object-fit: contain;
              border-radius: 8px;
            }
            .video_play_btn {
              background-image: url('https://cdn.extrastores.com/hybris/cdn/prod-19-mar-2023-upgradtion-01/_ui/responsive/theme-extra/images/play-button@2x.png');
              height: 100%;
              width: 100%;
              background-position: center;
              background-repeat: no-repeat;
              background-size: 65%;
              position: absolute;
              display: inline-block;
              line-height: 100%;
              vertical-align: top;
            }
          }
        }

        .slide-pagination-wrapper {
          position: absolute;
          bottom: 12px;
          left: 0;
          right: 0;
          margin-left: auto;
          margin-right: auto;
          text-align: center;
          .splide .splide__track {
            .splide__list {
              display: flex;
              justify-content: center;
              .splide__slide {
                border: 1px solid$color-grey-border;
                margin: 0 4px 0 4px;
                transition: all 0.2s linear;
                border-radius: 50%;
                &.is-active {
                  border: none;
                  background: $color-yellow;
                }
              }
            }
          }
        }
      }

      .more-item-btn__wrapper {
        position: absolute;
        bottom: 0px;
        padding-top: 20px;
        padding-bottom: 20px;
        background: linear-gradient(0deg, rgba(255, 255, 255, 0.48), rgba(255, 255, 255, 0.48));
        button {
          background: transparent;
          color: $icon-only-icon-blue;
          font-size: 12px;
          text-align: center;
          font-weight: 600;
          border: none;
        }
      }
      :global(.add-wishlist) {
        display: flex;
        width: 23px;
        height: 24px;
        background: $primary-white;
        border: 1px solid $secondary-gray-02;
        box-shadow: 0px 4px 12px $secondary-gray-02;
        border-radius: 6px;
        font-weight: 500;
        line-height: 16px;
        align-items: center;
        text-align: center;
        justify-content: center;
        @include desktop-min-width {
          border: 0;
          box-shadow: none;
        }
      }
    }

    .pd-image-container {
      height: 360px;
      .splide-wrapper {
        height: 100%;
        @include desktop-min-width {
          display: flex;
          gap: 16px;
        }
      }

      .images-wrapper {
        background-color: $color-white;
        display: flex;
        align-items: center;
        justify-content: center;
        max-width: 100vw;
        overflow: hidden;
        @include desktop-min-width {
          border-radius: 16px;
          max-width: 100%;
          min-height: 550px;
        }
        .main-slider-wrapper {
          @include desktop-min-width {
            display: flex;
            height: 376px;

            .splide__slide__container {
              padding-bottom: 16px;
            }
            .splide.splide--slide.splide--ltr {
              height: 100%;
              display: flex;
              align-items: center;
              .splide__track {
                margin-top: 10px;
              }
            }
          }
        }
      }

      .splide__slide__container {
        width: 100%;
        height: 100%;
        padding: 1rem;
        padding-top: 15px;
        @include desktop-min-width {
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
        }
      }

      .product-image {
        object-fit: contain;
        object-position: center;
        height: auto;
        max-height: 360px;
        transform: scale(1.2);
        @include desktop-min-width {
          width: 360px;
          max-height: 360px;
        }
        &-360 {
          @include desktop-min-width {
            width: 100%;
            height: 300px;
          }
        }
      }

      @include desktop-min-width {
        height: unset;
      }
    }
  }

  .side-wrapper {
    overflow-y: auto;
    min-width: 25%;
    width: auto;
    z-index: 1;
    display: flex;
    top: 10px;
    left: 16px;
    //left: 4.3%;
    max-height: -webkit-fill-available;
    &.rtl {
      left: unset;
      right: 16px;
      //right: 4.3%;
    }
  }
  * :global(.side-wrapper li) {
    border: none !important;
  }
  .side-wrapper::-webkit-scrollbar {
    width: 0;
    display: none;
  }

  .product_review_rating_section {
    position: absolute;
    right: 10px;
    top: 10px;
    display: flex;
    gap: 2px;
    z-index: 9;
    &.rtl {
      right: unset;
      left: 10px;
    }
  }

  :global {
    .splide__slide__container {
      .product-image-container {
        position: relative;
      }
    }
  }

  .skeleton-pdp-image {
    .skeleton-thumbnail-image {
      min-height: 350px;
      min-width: 56px;
      border-radius: 8px;
    }
    .skeleton-image-wrapper {
      min-height: 380px;
      background-color: #ddd;
    }
  }
</style>
